import React, { useState } from 'react';
import { 
  Building, 
  Mail, 
  Phone, 
  FileText, 
  Upload, 
  ChevronRight, 
  CheckCircle2,
  ShieldCheck,
  X
} from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { toast } from 'sonner';
import api from '../../lib/api';
import { cn } from '../../lib/utils';

interface OnboardingModalProps {
  onComplete: () => void;
  onSkip: () => void;
}

export function OnboardingModal({ onComplete, onSkip }: OnboardingModalProps) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    business_name: '',
    business_email: '',
    business_phone: '',
    document_type: 'Business License',
    document: null as File | null,
  });

  const handleSubmitBusiness = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/onboarding/complete', {
        role: 'manufacturer',
        business_name: formData.business_name,
        business_email: formData.business_email,
        business_phone: formData.business_phone,
      });
      setStep(2);
    } catch (err: any) {
      toast.error('Failed to save business details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.document) {
      toast.error('Please upload a document');
      return;
    }
    setLoading(true);
    try {
      const data = new FormData();
      data.append('document_type', formData.document_type);
      data.append('document', formData.document);

      await api.post('/manufacturer/verification', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Onboarding completed!');
      onComplete();
    } catch (err: any) {
      toast.error('Failed to upload verification document');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in duration-300">
      <div className="bg-white w-full max-w-lg rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-500 relative">
        <button 
          onClick={onSkip}
          className="absolute top-6 right-6 p-2 hover:bg-gray-100 rounded-full transition-colors z-10"
        >
          <X className="h-5 w-5 text-gray-400" />
        </button>

        <div className="p-8 sm:p-10">
          <div className="flex flex-col items-center text-center mb-8">
            <div className="h-16 w-16 rounded-2xl bg-emerald-600 text-white flex items-center justify-center shadow-xl shadow-emerald-200 mb-4">
              <ShieldCheck className="h-8 w-8" />
            </div>
            <h2 className="text-2xl font-extrabold text-gray-900">Complete Onboarding</h2>
            <p className="text-sm text-gray-500 mt-2 font-medium">Verify your business to start selling on Shoplycircle</p>
          </div>

          <div className="flex items-center justify-center gap-2 mb-8">
            <div className={cn("h-1.5 w-12 rounded-full transition-all duration-500", step >= 1 ? "bg-emerald-600" : "bg-gray-100")} />
            <div className={cn("h-1.5 w-12 rounded-full transition-all duration-500", step >= 2 ? "bg-emerald-600" : "bg-gray-100")} />
          </div>

          {step === 1 && (
            <form onSubmit={handleSubmitBusiness} className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Business Name</label>
                  <div className="relative">
                    <Building className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      placeholder="Acme Manufacturing"
                      className="pl-12 h-12 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-emerald-500"
                      value={formData.business_name}
                      onChange={e => setFormData({...formData, business_name: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Business Email</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type="email"
                      placeholder="business@example.com"
                      className="pl-12 h-12 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-emerald-500"
                      value={formData.business_email}
                      onChange={e => setFormData({...formData, business_email: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Business Phone</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      placeholder="+977 98XXXXXXXX"
                      className="pl-12 h-12 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-emerald-500"
                      value={formData.business_phone}
                      onChange={e => setFormData({...formData, business_phone: e.target.value})}
                      required
                    />
                  </div>
                </div>
              </div>
              <Button type="submit" loading={loading} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl font-bold shadow-lg shadow-emerald-200 group">
                Next Step <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleSubmitVerification} className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Document Type</label>
                  <select 
                    className="w-full h-12 px-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-emerald-500 text-sm font-medium"
                    value={formData.document_type}
                    onChange={e => setFormData({...formData, document_type: e.target.value})}
                  >
                    <option>Business License</option>
                    <option>Tax ID / PAN Certificate</option>
                    <option>VAT Registration</option>
                  </select>
                </div>
                
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Upload Document</label>
                  <div className="relative h-40 border-2 border-dashed border-gray-100 rounded-[2rem] flex flex-col items-center justify-center bg-gray-50/50 hover:bg-gray-50 transition-colors group cursor-pointer">
                    <input 
                      type="file" 
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      onChange={e => setFormData({...formData, document: e.target.files?.[0] || null})}
                      accept=".pdf,.jpg,.jpeg,.png"
                    />
                    {formData.document ? (
                      <div className="flex flex-col items-center">
                        <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 mb-2">
                          <FileText className="h-6 w-6" />
                        </div>
                        <p className="text-xs font-bold text-gray-900">{formData.document.name}</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center text-center px-6">
                        <Upload className="h-8 w-8 text-gray-300 mb-2 group-hover:text-emerald-500 transition-colors" />
                        <p className="text-xs font-bold text-gray-900">Click to upload document</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <Button type="submit" loading={loading} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl font-bold shadow-lg shadow-emerald-200">
                Complete Onboarding <CheckCircle2 className="ml-2 h-5 w-5" />
              </Button>
            </form>
          )}

          <button 
            onClick={onSkip}
            className="w-full mt-4 text-xs font-bold text-gray-400 hover:text-gray-600 transition-colors py-2"
          >
            I'll do this later
          </button>
        </div>
      </div>
    </div>
  );
}
